import {useState} from 'react';
import {Tabs, TabsContent, TabsList, TabsTrigger} from './ui/tabs';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from './ui/card';
import {Button} from './ui/button';
import {Input} from './ui/input';
import {Label} from './ui/label';
import {Eye, EyeOff, Mail, Lock, User, AlertCircle, ArrowLeft} from 'lucide-react';
import LegalPages from './LegalPages';

interface AuthFormProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onRegister: (name: string, email: string, password: string) => Promise<void>;
  onResetPassword?: (email: string) => Promise<void>;
}

interface FormErrors {
  email?: string;
  password?: string;
  name?: string;
  general?: string;
}

export default function AuthForm({onLogin, onRegister, onResetPassword}: AuthFormProps) {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [legalPageType, setLegalPageType] = useState<'terms' | 'privacy' | null>(null);
  const [showAccountExistsMessage, setShowAccountExistsMessage] = useState(false);
  const [existingAccountEmail, setExistingAccountEmail] = useState('');
  const [accountExistsEmailSent, setAccountExistsEmailSent] = useState(false);

  // État des formulaires
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: ''
  });

  const [resetEmail, setResetEmail] = useState('');

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    return password.length >= 6;
  };

  const validateLoginForm = () => {
    const newErrors: FormErrors = {};

    if (!loginForm.email) {
      newErrors.email = 'L\'email est requis';
    } else if (!validateEmail(loginForm.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (!loginForm.password) {
      newErrors.password = 'Le mot de passe est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRegisterForm = () => {
    const newErrors: FormErrors = {};

    if (!registerForm.name) {
      newErrors.name = 'Le nom est requis';
    }

    if (!registerForm.email) {
      newErrors.email = 'L\'email est requis';
    } else if (!validateEmail(registerForm.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (!registerForm.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (!validatePassword(registerForm.password)) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateLoginForm()) return;

    setIsLoading(true);
    try {
      await onLogin(loginForm.email, loginForm.password);
    } catch (error: any) {
      setErrors({general: error.message || 'Email ou mot de passe incorrect'});
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setShowAccountExistsMessage(false);

    if (!validateRegisterForm()) return;

    setIsLoading(true);
    try {
      await onRegister(registerForm.name, registerForm.email, registerForm.password);
    } catch (error: any) {
      // Vérifier si c'est une erreur de compte existant
      if (error.message && error.message.includes('existe déjà')) {
        setShowAccountExistsMessage(true);
        setExistingAccountEmail(registerForm.email);
        setErrors({});
      } else {
        setErrors({general: error.message || 'Une erreur est survenue lors de la création du compte'});
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateEmail(resetEmail)) {
      setErrors({email: 'Email invalide'});
      return;
    }

    setIsLoading(true);
    try {
      if (onResetPassword) {
        await onResetPassword(resetEmail);
      }
      // Toujours afficher le même message pour la sécurité
      setResetEmailSent(true);
      // Masquer le message après 5 secondes et revenir à la connexion
      setTimeout(() => {
        setResetEmailSent(false);
        setShowResetPassword(false);
        setResetEmail('');
      }, 5000);
    } catch (error) {
      // Ne pas révéler si l'email existe ou non
      setResetEmailSent(true);
      // Masquer le message après 5 secondes et revenir à la connexion
      setTimeout(() => {
        setResetEmailSent(false);
        setShowResetPassword(false);
        setResetEmail('');
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour envoyer directement l'email de réinitialisation depuis le message de compte existant
  const handleDirectResetPassword = async () => {
    setIsLoading(true);
    try {
      if (onResetPassword) {
        await onResetPassword(existingAccountEmail);
      }
      // Afficher le message de succès
      setAccountExistsEmailSent(true);
    } catch (error) {
      // Toujours afficher le message de succès pour la sécurité
      setAccountExistsEmailSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Afficher la page légale si sélectionnée
  if (legalPageType) {
    return (
      <LegalPages
        type={legalPageType}
        onBack={() => setLegalPageType(null)}
      />
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        {/* Logo et titre */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center">
            <img src="/logo.png" alt="Alerte Faune" className="h-72 w-auto object-contain rounded-3xl" />
          </div>
        </div>

        {/* Vue réinitialisation mot de passe */}
        {showResetPassword ? (
          <Card className="border border-gray-100/50 shadow-2xl rounded-3xl bg-white/95 backdrop-blur-sm">
            <CardHeader className="pb-4 pt-8 px-8">
              <CardTitle className="text-center text-2xl font-semibold text-gray-900">
                Réinitialiser le mot de passe
              </CardTitle>
              <CardDescription className="text-center text-gray-500 mt-2">
                Entrez votre email pour recevoir un lien de réinitialisation
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8 pt-4">
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-sm font-medium text-gray-700">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      id="reset-email"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="votre@email.fr"
                      className="pl-10 h-12 text-base rounded-xl border-gray-200 focus:border-primary focus:ring-primary"
                      required
                    />
                  </div>
                  {errors.email && (
                    <p className="text-sm text-destructive flex items-center gap-1 mt-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Message de succès */}
                {resetEmailSent && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-green-900">Email envoyé !</h4>
                        <p className="text-sm text-green-700 mt-1">
                          Si cet email existe dans notre base, vous recevrez un lien de réinitialisation dans quelques minutes.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading || resetEmailSent}
                  className="w-full h-12 text-base font-medium rounded-xl bg-gray-900 hover:bg-gray-800 text-white disabled:opacity-50 transition-all duration-200"
                >
                  {isLoading ? 'Envoi en cours...' : 'Réinitialiser mon mot de passe'}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowResetPassword(false);
                    setResetEmail('');
                    setResetEmailSent(false);
                    setErrors({});
                  }}
                  className="w-full h-12 text-base font-medium rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour à la connexion
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          /* Formulaires de connexion/inscription */
          <Card className="border border-gray-100/50 shadow-2xl rounded-3xl bg-white/95 backdrop-blur-sm">
            <CardHeader className="pb-4 pt-8 px-8">
              <CardTitle className="text-center text-2xl font-semibold text-gray-900">
                {activeTab === 'login' ? 'Connexion' : 'Créer un compte'}
              </CardTitle>
              <CardDescription className="text-center text-gray-500 mt-2">
                {activeTab === 'login'
                  ? 'Accédez à votre compte'
                  : 'Rejoignez la communauté'
                }
              </CardDescription>
            </CardHeader>

            <CardContent className="px-8 pb-8">
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'login' | 'register')}>
                <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-100 p-1 rounded-full">
                  <TabsTrigger
                    value="login"
                    className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all duration-200"
                  >
                    Connexion
                  </TabsTrigger>
                  <TabsTrigger
                    value="register"
                    className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all duration-200"
                  >
                    Inscription
                  </TabsTrigger>
                </TabsList>

                {/* Formulaire de connexion */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="votre@email.com"
                          value={loginForm.email}
                          onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                          className="pl-10"
                          aria-invalid={errors.email ? 'true' : 'false'}
                        />
                      </div>
                      {errors.email && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="login-password">Mot de passe</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={loginForm.password}
                          onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                          className="pl-10 pr-10"
                          aria-invalid={errors.password ? 'true' : 'false'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.password}
                        </p>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => setShowResetPassword(true)}
                        className="p-0 h-auto"
                      >
                        Mot de passe oublié ?
                      </Button>
                    </div>

                    {/* Erreur générale pour la connexion */}
                    {errors.general && activeTab === 'login' && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm">{errors.general}</span>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full bg-black hover:bg-gray-800 text-white shadow-lg hover:shadow-xl transition-all duration-200 rounded-2xl h-12 font-medium"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Connexion...' : 'Se connecter'}
                    </Button>
                  </form>
                </TabsContent>

                {/* Formulaire d'inscription */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Nom complet</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="Jean Dupont"
                          value={registerForm.name}
                          onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                          className="pl-10"
                          aria-invalid={errors.name ? 'true' : 'false'}
                        />
                      </div>
                      {errors.name && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="votre@email.com"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                          className="pl-10"
                          aria-invalid={errors.email ? 'true' : 'false'}
                        />
                      </div>
                      {errors.email && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="register-password">Mot de passe</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                          id="register-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={registerForm.password}
                          onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                          className="pl-10 pr-10"
                          aria-invalid={errors.password ? 'true' : 'false'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.password}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Minimum 6 caractères
                      </p>
                    </div>

                    {/* Message de compte existant */}
                    {showAccountExistsMessage && activeTab === 'register' && (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
                        {!accountExistsEmailSent ? (
                          <>
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <AlertCircle className="w-5 h-5 text-blue-600" />
                              </div>
                              <div className="flex-1">
                                <h4 className="text-sm font-medium text-blue-900">Un compte existe déjà</h4>
                                <p className="text-sm text-blue-700 mt-1">
                                  Un compte avec l'email <strong>{existingAccountEmail}</strong> existe déjà.
                                </p>
                                <p className="text-sm text-blue-700 mt-1">
                                  Il a peut-être été créé pour vous. Réinitialisez votre mot de passe pour accéder à votre compte.
                                </p>
                              </div>
                            </div>
                            <Button
                              type="button"
                              onClick={handleDirectResetPassword}
                              disabled={isLoading}
                              className="w-full h-10 text-sm font-medium rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200"
                            >
                              {isLoading ? 'Envoi en cours...' : 'Réinitialiser mon mot de passe'}
                            </Button>
                          </>
                        ) : (
                          <>
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <h4 className="text-sm font-medium text-green-900">Email envoyé !</h4>
                                <p className="text-sm text-green-700 mt-1">
                                  Un email de réinitialisation a été envoyé à <strong>{existingAccountEmail}</strong>.
                                </p>
                                <p className="text-sm text-green-700 mt-1">
                                  Vérifiez votre boîte mail et suivez les instructions.
                                </p>
                              </div>
                            </div>
                            <Button
                              type="button"
                              onClick={() => {
                                setActiveTab('login');
                                setShowAccountExistsMessage(false);
                                setAccountExistsEmailSent(false);
                                setExistingAccountEmail('');
                                // Pré-remplir l'email de connexion
                                setLoginForm({...loginForm, email: existingAccountEmail});
                              }}
                              variant="outline"
                              className="w-full h-10 text-sm font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
                            >
                              <ArrowLeft className="w-4 h-4" />
                              Retour à la connexion
                            </Button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Erreur générale pour l'inscription */}
                    {errors.general && activeTab === 'register' && !showAccountExistsMessage && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm">{errors.general}</span>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full bg-black hover:bg-gray-800 text-white shadow-lg hover:shadow-xl transition-all duration-200 rounded-2xl h-12 font-medium"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Création...' : 'Créer un compte'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Informations légales */}
        <div className="text-center mt-6">
          <p className="text-xs text-muted-foreground">
            En vous connectant, vous acceptez nos{' '}
            <button
              type="button"
              onClick={() => setLegalPageType('terms')}
              className="text-xs text-primary underline hover:text-primary/80 transition-colors"
            >
              conditions d'utilisation
            </button>
            <br />
            et notre{' '}
            <button
              type="button"
              onClick={() => setLegalPageType('privacy')}
              className="text-xs text-primary underline hover:text-primary/80 transition-colors"
            >
              politique de confidentialité
            </button>
            .
          </p>
        </div>
      </div>
    </div>
  );
}